
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { SmsCustomer } from '@/hooks/sms/types';
import { 
  LineChart, Line, PieChart, Pie, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  Cell
} from 'recharts';
import { format, parseISO, isValid, addDays, differenceInDays, startOfDay } from 'date-fns';

interface SubscriptionAnalyticsProps {
  subscribers: SmsCustomer[];
  selectedRestaurants: string[];
}

const SubscriptionAnalytics: React.FC<SubscriptionAnalyticsProps> = ({
  subscribers,
  selectedRestaurants
}) => {
  // Count total and subscription stats
  const totalSubscribers = subscribers.length;
  const subscribedCount = subscribers.filter(s => s.status === 'subscribed').length;
  const unsubscribedCount = subscribers.filter(s => s.status === 'unsubscribed').length;
  const invalidCount = subscribers.filter(s => s.status === 'invalid' || s.status === 'failed' || s.status === 'not_synced').length;
  
  // Calculate opt-in rate
  const optInRate = totalSubscribers ? (subscribedCount / totalSubscribers) * 100 : 0;
  
  // Generate daily subscription trend data across full date range
  const trendData = useMemo(() => {
    if (!subscribers.length) return [];
    
    console.log('Generating daily trend data using real timestamps for', subscribers.length, 'subscribers');
    
    // Debug: Check data quality with timestamp fields
    const dataQualityStats = {
      total: subscribers.length,
      withOptInDate: subscribers.filter(s => s.opt_in_date).length,
      withOptOutDate: subscribers.filter(s => s.opt_out_date).length,
      withOptOutSource: subscribers.filter(s => s.opt_out_source).length,
      activeSubscribers: subscribers.filter(s => s.status === 'subscribed').length,
      unsubscribedSubscribers: subscribers.filter(s => s.status === 'unsubscribed').length
    };
    console.log('Real timestamp data quality:', dataQualityStats);
    
    // Find the earliest opt-in date and latest date (today)
    let earliestDate = new Date();
    let latestDate = new Date();
    
    const validOptInDates = [];
    const validOptOutDates = [];
    
    // Collect all valid opt-in dates
    subscribers.forEach(subscriber => {
      const optInDate = subscriber.opt_in_date;
      if (optInDate && optInDate !== 'Never') {
        try {
          const optInDateParsed = parseISO(optInDate);
          if (isValid(optInDateParsed)) {
            validOptInDates.push(optInDateParsed);
            if (optInDateParsed < earliestDate) {
              earliestDate = optInDateParsed;
            }
          }
        } catch (error) {
          console.warn('Invalid opt-in date:', subscriber.id, optInDate, error);
        }
      }
    });
    
    // Collect all valid opt-out dates
    subscribers.forEach(subscriber => {
      const optOutDate = subscriber.opt_out_date;
      if (optOutDate && subscriber.status === 'unsubscribed') {
        try {
          const optOutDateParsed = parseISO(optOutDate);
          if (isValid(optOutDateParsed)) {
            validOptOutDates.push(optOutDateParsed);
          }
        } catch (error) {
          console.warn('Invalid opt-out date:', subscriber.id, optOutDate, error);
        }
      }
    });
    
    // If no valid dates found, return empty array
    if (validOptInDates.length === 0) {
      console.warn('No valid opt-in dates found');
      return [];
    }
    
    // Normalize dates to start of day
    earliestDate = startOfDay(earliestDate);
    latestDate = startOfDay(new Date());
    
    // Calculate total days in the range
    const totalDays = differenceInDays(latestDate, earliestDate) + 1;
    
    console.log(`Creating daily chart from ${format(earliestDate, 'yyyy-MM-dd')} to ${format(latestDate, 'yyyy-MM-dd')} (${totalDays} days)`);
    
    // Create daily buckets
    const dailyData = [];
    
    for (let i = 0; i < totalDays; i++) {
      const currentDate = addDays(earliestDate, i);
      const dayKey = format(currentDate, 'yyyy-MM-dd');
      
      // Count opt-ins and opt-outs for this specific day
      const dayOptIns = validOptInDates.filter(date => 
        format(startOfDay(date), 'yyyy-MM-dd') === dayKey
      ).length;
      
      const dayOptOuts = validOptOutDates.filter(date => 
        format(startOfDay(date), 'yyyy-MM-dd') === dayKey
      ).length;
      
      dailyData.push({
        date: dayKey,
        displayDate: format(currentDate, 'MMM d'),
        newOptIns: dayOptIns,
        newOptOuts: dayOptOuts
      });
    }
    
    // Calculate cumulative totals for each day
    let runningTotal = 0;
    let runningActive = 0;
    
    const result = dailyData.map(day => {
      runningTotal += day.newOptIns;
      runningActive += day.newOptIns - day.newOptOuts;
      
      // Calculate current counts for this date point
      const currentActiveCount = Math.max(0, runningActive);
      const currentOptedOutCount = Math.max(0, runningTotal - runningActive);
      
      return {
        date: day.date,
        displayDate: day.displayDate,
        totalSubscribers: runningTotal,
        activeSubscribers: currentActiveCount,
        optedOut: currentOptedOutCount,
        invalid: 0, // Will be set correctly in the final adjustment
        newOptIns: day.newOptIns,
        newOptOuts: day.newOptOuts
      };
    });
    
    // For large datasets, sample every N days to avoid overcrowding the chart
    let finalResult = result;
    if (result.length > 90) {
      // If more than 90 days, sample every 7 days (weekly)
      const sampleRate = Math.ceil(result.length / 52); // Aim for ~52 data points (weekly over a year)
      finalResult = result.filter((_, index) => index % sampleRate === 0 || index === result.length - 1);
      console.log(`Sampling chart data: ${result.length} days → ${finalResult.length} points (every ${sampleRate} days)`);
    }
    
    // Ensure the final data point matches current reality
    if (finalResult.length > 0) {
      const lastIndex = finalResult.length - 1;
      const currentActiveCount = subscribers.filter(s => s.status === 'subscribed').length;
      const currentUnsubscribedCount = subscribers.filter(s => s.status === 'unsubscribed').length;
      const currentInvalidCount = subscribers.filter(s => s.status === 'invalid' || s.status === 'failed' || s.status === 'not_synced').length;
      const currentTotalCount = subscribers.length;
      
      // Adjust the final point to match actual current counts
      finalResult[lastIndex] = {
        ...finalResult[lastIndex],
        totalSubscribers: currentTotalCount,
        activeSubscribers: currentActiveCount,
        optedOut: currentUnsubscribedCount,
        invalid: currentInvalidCount
      };
    }
    
    console.log('Final daily trend data:', {
      originalDays: result.length,
      sampledPoints: finalResult.length,
      dateRange: `${result[0]?.date} to ${result[result.length - 1]?.date}`,
      totalSubscribers: finalResult[finalResult.length - 1]?.totalSubscribers,
      activeSubscribers: finalResult[finalResult.length - 1]?.activeSubscribers,
      correctedToMatchSummary: true
    });
    
    return finalResult;
  }, [subscribers]);
  
  // Subscription status data for pie chart
  const statusData = [
    { name: 'Active Subscribers', value: subscribedCount, color: '#22c55e' },
    { name: 'Opted Out', value: unsubscribedCount, color: '#ef4444' },
    { name: 'Invalid', value: invalidCount, color: '#94a3b8' },
  ].filter(item => item.value > 0); // Only show categories with data
  

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold mb-2">{totalSubscribers}</div>
            <p className="text-sm text-muted-foreground">Total Subscribers</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold mb-2">{subscribedCount}</div>
            <p className="text-sm text-muted-foreground">Active Subscribers</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold mb-2">{optInRate.toFixed(1)}%</div>
            <p className="text-sm text-muted-foreground">Opt-in Rate</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold mb-2">
              {subscribers.length > 0 ? 
                '$' + (subscribers.reduce((sum, sub) => {
                  const spend = typeof sub.totalSpend === 'number' 
                    ? sub.totalSpend 
                    : parseFloat(sub.totalSpend || '0');
                  return sum + spend;
                }, 0) / subscribers.length).toFixed(2) : '$0.00'}
            </div>
            <p className="text-sm text-muted-foreground">Avg. Spend per Subscriber</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Subscription Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Subscriber Growth Trend</CardTitle>
          <CardDescription>Complete subscription timeline using real opt-in and opt-out timestamps - daily data from first subscription to present</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={trendData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="displayDate" 
                  tick={{ fontSize: 12 }}
                  interval="preserveStartEnd"
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(label, payload) => {
                    const data = payload?.[0]?.payload;
                    return data ? `${data.displayDate} (${data.date})` : label;
                  }}
                  formatter={(value, name) => [value, name]}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="totalSubscribers" 
                  stroke="#8884d8" 
                  activeDot={{ r: 8 }} 
                  name="Total Subscribers"
                />
                <Line 
                  type="monotone" 
                  dataKey="activeSubscribers" 
                  stroke="#22c55e" 
                  name="Active Subscribers"
                />
                <Line 
                  type="monotone" 
                  dataKey="optedOut" 
                  stroke="#ef4444" 
                  name="Opted Out"
                />
                <Line 
                  type="monotone" 
                  dataKey="invalid" 
                  stroke="#94a3b8" 
                  name="Invalid"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      
      {/* Subscription Status Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription Status</CardTitle>
          <CardDescription>Distribution of subscriber status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[400px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} subscribers`, '']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionAnalytics;
